import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Avatar,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDashboardControllers } from '../DashboardControllers';
import * as filestack from 'filestack-js';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getTransformedImageUrl } from '../../../utils/filestack';

const filestackClient = filestack.init('AbWBvsKDKTkqSnuVP4OVGz');

const ROLES = {
  DRIVER: 'driver',
  MECHANIC: 'mechanic',
  MANAGEMENT: 'management'
};

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'ca', name: 'Catalan' }
];

const ProfileSection = () => {
  const { t, i18n } = useTranslation('common');
  const { user, loading, updateProfile } = useDashboardControllers();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(!isMobile);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    name: user?.name || '',
    role: user?.role || ROLES.DRIVER,
    language: user?.language || 'en',
    password: '',
    confirmPassword: '',
    imageUrl: user?.imageUrl || ''
  });
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    // Update language when user preference changes
    if (user?.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language, i18n]);

  useEffect(() => {
    // Update form data when user data changes
    if (user) {
      setFormData(prev => ({
        ...prev,
        username: user.username || prev.username,
        email: user.email || prev.email,
        name: user.name || prev.name,
        role: user.role || prev.role,
        language: user.language || prev.language,
        imageUrl: user.imageUrl || prev.imageUrl
      }));
    }
  }, [user]);

  // Update expanded state when screen size changes
  useEffect(() => {
    setExpanded(!isMobile);
  }, [isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async () => {
    try {
      setUploadLoading(true);
      await filestackClient.picker({
        accept: ['image/*'],
        maxFiles: 1,
        uploadInBackground: false,
        onUploadDone: async (res) => {
          if (res.filesUploaded && res.filesUploaded.length > 0) {
            const imageUrl = res.filesUploaded[0].url;
            console.log('Image uploaded successfully:', imageUrl);
            
            setFormData(prev => ({
              ...prev,
              imageUrl
            }));
            
            // Immediately update the profile with the new image
            try {
              await updateProfile({ imageUrl });
              toast.success(t('profile.imageUpdated'));
            } catch (error) {
              console.error('Error updating profile with new image:', error);
              toast.error(t('profile.imageUploadError'));
            }
          } else {
            console.error('No files uploaded or upload response is invalid');
            toast.error(t('profile.imageUploadError'));
          }
        },
      }).open();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('profile.imageUploadError'));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const updateData = {
      username: formData.username,
      email: formData.email,
      name: formData.name,
      role: formData.role,
      language: formData.language
    };

    // Only include imageUrl if it exists and is not empty
    if (formData.imageUrl) {
      updateData.imageUrl = formData.imageUrl;
      console.log('Including imageUrl in profile update:', formData.imageUrl);
    }

    if (formData.password) {
      updateData.password = formData.password;
    }

    try {
      await updateProfile(updateData);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  return (
    <Paper sx={{ p: 0 }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="profile-content"
          id="profile-header"
          sx={{ px: 3, py: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src={formData.imageUrl ? getTransformedImageUrl(formData.imageUrl, 'thumbnail') : formData.imageUrl} 
              alt={formData.name || formData.username}
              sx={{ width: 40, height: 40, mr: 2 }}
            />
            <Typography variant="h6">
              {t('profile.settings')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, pt: 0 }}>
          {/* Profile Image Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, mt: 2 }}>
            <Avatar 
              src={formData.imageUrl ? getTransformedImageUrl(formData.imageUrl, 'thumbnail') : formData.imageUrl} 
              alt={formData.name || formData.username}
              sx={{ width: 100, height: 100, mb: 2 }}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={handleImageUpload}
              disabled={uploadLoading || loading}
            >
              {uploadLoading ? <CircularProgress size={24} /> : t('profile.uploadPhoto')}
            </Button>
          </Box>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.username')}
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              fullWidth
              label={t('profile.fullName')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('profile.role.label')}</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={loading}
              >
                <MenuItem value={ROLES.DRIVER}>{t('profile.role.driver')}</MenuItem>
                <MenuItem value={ROLES.MECHANIC}>{t('profile.role.mechanic')}</MenuItem>
                <MenuItem value={ROLES.MANAGEMENT}>{t('profile.role.management')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>{t('profile.language')}</InputLabel>
              <Select
                name="language"
                value={formData.language}
                onChange={handleChange}
                disabled={loading}
              >
                {LANGUAGES.map(lang => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {t(`languages.${lang.code}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <TextField
              margin="normal"
              fullWidth
              label={t('profile.newPassword')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              helperText={t('profile.keepCurrentPassword')}
            />
            {formData.password && (
              <TextField
                margin="normal"
                fullWidth
                label={t('profile.confirmNewPassword')}
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('profile.updateProfile')}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default ProfileSection; 